<template>
  <div>
    <v-tabs
      v-model="tab"
      grow
      hide-slider
    >
      <v-tab
        v-if="field && (!field.subType || field.subType === 'date')"
      >
        <v-icon>mdi-calendar</v-icon>
      </v-tab>
      <v-tab
        v-if="field && (!field.subType || field.subType === 'time')"
      >
        <v-icon>mdi-clock</v-icon>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item
        v-if="field && (!field.subType || field.subType === 'date')"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            v-model="datePickerLabel"
            readonly
            hide-details
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="date"
          first-day-of-week="1"
          :min="minDate"
          :max="maxDate"
          @dblclick:date="onDateDblClick"
        />
      </v-tab-item>
      <v-tab-item
        v-if="field && (!field.subType || field.subType === 'time')"
      >
        <v-time-picker
          :key="timePickerKey"
          v-model="time"
          :format="field && field.timeFormat ? field.timeFormat : '24hr'"
          :min="minTime"
          :max="maxTime"
          scrollable
        />
      </v-tab-item>
    </v-tabs-items>
    <v-row no-gutters>
      <v-col class="white text-right pb-2 px-2">
        <v-spacer />
        <v-btn
          text
          color="primary"
          @click="applyDate"
        >
          {{ $t('base.ok') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { dateService, FORMATS } from '@/global/services/date'

export default {
  name: 'DatetimePicker',

  props: {
    value: {
      type: String,
      default: null
    },
    field: {
      type: Object,
      default: () => ({})
    }
  },

  emits: [
    'input',
    'close',
    'applied-date',
    'dblclick:date',
    'date-change'
  ],

  data () {
    return {
      tab: null,
      date: null,
      time: null,
      datePickerLabel: null,
      lastDateTime: null,
      timePickerKey: 0,
      timeManuallySet: false,
      isInternalTimeChange: false
    }
  },

  computed: {
    getDateTimeFormat () {
      if (this.field && this.field.format) {
        return this.field.format
      }

      // Without an explicit format, a date-only field shouldn't fall back to
      // a format that still includes a time component (which is how a
      // "00:00" ends up tacked onto the selected date).
      return this.field && this.field.subType === 'date' ? FORMATS.date : FORMATS.datetime
    },

    getDateFormat () {
      return this.getDateTimeFormat ? this.getDateTimeFormat.split(' ')[0] : undefined
    },

    /**
     * Apply min date for date picker.
     *
     * * Valid date format is defined by {@link FORMATS.isoDate}.
     * @return {*|undefined}
     */
    minDate () {
      const { minDate, disablePastDates } = this.field || {}
      // if past dates need to be disabled, set current date as a min selectable date
      if (disablePastDates) {
        return dateService.today(FORMATS.isoDate)
      }

      // firstly validate provided min date and then apply
      return dateService.isValid(minDate, FORMATS.isoDate, true) ? minDate : undefined
    },

    /**
     * Apply max date for date picker.
     *
     * * Valid date format is defined by {@link FORMATS.isoDate}.
     * @return {*|undefined}
     */
    maxDate () {
      const { maxDate, disableFutureDates } = this.field || {}
      // if future dates need to be disabled, set current date as a max selectable date
      if (disableFutureDates) {
        return dateService.today(FORMATS.isoDate)
      }

      // firstly validate provided max date and then apply
      return dateService.isValid(maxDate, FORMATS.isoDate, true) ? maxDate : undefined
    },

    /**
     * Apply min time for time picker.
     * @return {*|undefined}
     */
    minTime () {
      const { minTime, disablePastTime } = this.field || {}
      // if past-time need to be disabled, set current time as a min selectable time
      if (disablePastTime) {
        return dateService.currentTime(FORMATS.timeShort)
      }

      // firstly validate provided min time and then apply
      return dateService.isValid(minTime, FORMATS.timeShort, true) ? minTime : undefined
    },

    /**
     * Apply max time for time picker.
     * @return {*|undefined}
     */
    maxTime () {
      const { maxTime, disableFutureTime } = this.field || {}
      // if future time need to be disabled, set current time as a max selectable time
      if (disableFutureTime) {
        return dateService.currentTime(FORMATS.timeShort)
      }

      // firstly validate provided max time and then apply
      return dateService.isValid(maxTime, FORMATS.timeShort, true) ? maxTime : undefined
    }
  },

  watch: {
    date (date, oldDate) {
      this.datePickerLabel = dateService.format(date, FORMATS.datetime)
      this.$emit('date-change', date)

      if (!this.isInternalTimeChange && oldDate && !this.timeManuallySet) {
        this.isInternalTimeChange = true
        this.time = null
        this.timePickerKey++
        this.$nextTick(() => {
          this.isInternalTimeChange = false
        })
      }
    },

    time (value, oldValue) {
      if (!this.isInternalTimeChange && oldValue) {
        this.timeManuallySet = true
      }
    },

    minTime () {
      this.timePickerKey++
    },

    maxTime () {
      this.timePickerKey++
    },

    value: {
      immediate: true,
      handler (value) {
        if (value) {
          const { format = FORMATS.datetime } = this.field || {}
          if (dateService.isValid(value, format, true)) {
            this.updatePickers(value)
          }
          else {
            this.updatePickers()
          }
        }
        else {
          // Reset the pickers' own internal selection when the value is
          // cleared - otherwise re-opening the picker after a clear still
          // shows whatever date/time was previously selected.
          this.date = null
          this.time = null
          this.datePickerLabel = null
          this.lastDateTime = null
          this.timeManuallySet = false
        }
      }
    }
  },

  mounted () {
    document.addEventListener('keydown', this.enterAndEscapeEventHandler)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this.enterAndEscapeEventHandler)
  },

  methods: {
    onDateDblClick () {
      this.$emit('dblclick:date', this)
    },

    enterAndEscapeEventHandler (event) {
      const { closeWithEscapeEnter } = this.field || {}

      // Close on enter or escape only if closeWithEscapeEnter is provided from field config
      if (!closeWithEscapeEnter) {
        return
      }

      if (event.key === 'enter') {
        this.updateFinalDatetimeValue()
        this.$emit('close')
      }

      else if (event.key === 'Escape') {
        this.$emit('close')
      }
    },

    updateFinalDatetimeValue () {
      const finalDate = this.date ? this.date : null
      const { defaultTime } = this.field || {}
      let finalTime = this.time

      if (!finalTime && !this.timeManuallySet) {
        if (typeof defaultTime === 'function') {
          finalTime = defaultTime(finalDate)
        }
        else if (defaultTime && typeof defaultTime === 'string') {
          finalTime = defaultTime
        }
      }

      finalTime = finalTime || '00:00'

      if (this.field.subType) {
        if (this.field.subType === 'time') {
          this.lastDateTime = finalTime
          this.$emit('input', this.lastDateTime)
        }
        else if (this.field.subType === 'date') {
          this.lastDateTime = dateService.format(finalDate, this.getDateTimeFormat)
          this.$emit('input', this.lastDateTime)
        }
      }
      else if (finalDate) {
        this.lastDateTime = dateService.format(finalDate + ' ' + finalTime, this.getDateTimeFormat)
        // Emit final date-time in provided date-time format.
        // if date-time format is not provided, default date-time format will be applied 'DD.MM.YYYY HH:mm:ss'
        this.$emit('input', this.lastDateTime)
      }
    },

    updatePickers (value) {
      if (value && Date.parse(value) !== Date.parse(this.lastDateTime)) {
        this.isInternalTimeChange = true
        this.timeManuallySet = false

        if (this.field && this.field.subType) {
          if (this.field.subType === 'time') {
            this.time = value
          }
          else if (this.field.subType === 'date') {
            this.date = dateService.formatCustom(value, this.getDateFormat, FORMATS.isoDate)
          }
        }
        else {
          this.date = dateService.formatCustom(value, this.getDateFormat, FORMATS.isoDate)
          this.time = value.split(' ')[1]
        }

        this.$nextTick(() => {
          this.isInternalTimeChange = false
        })
      }
    },

    applyDate () {
      this.updateFinalDatetimeValue()
      this.$emit('close')
      this.$emit('applied-date')
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep.v-input {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px !important;
  line-height: 20px !important;
  color: #111827 !important;
  font-weight: 500 !important;
}
::v-deep.v-input--is-disabled > .v-input__control > .v-input__slot > .v-text-field__slot > input {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px !important;
  line-height: 20px !important;
  color: #9CA3AF !important;
  font-weight: 400 !important;
}

::v-deep.v-input > .v-input__control > .v-input__slot > .v-input__prepend-inner > .v-input__icon--prepend-inner > .v-icon {
  color: rgba(0, 0, 0, 0.38) !important;
}

::v-deep {
  .v-input--is-disabled > .v-input__control > .v-input__slot > .v-text-field__slot > input {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px !important;
    line-height: 20px !important;
    color: #9CA3AF !important;
    font-weight: 400 !important;
  }

  .v-input > .v-input__control > .v-input__slot > .v-input__prepend-inner > .v-input__icon > .v-icon {
    color: rgba(0, 0, 0, 0.38) !important;
  }

  .v-text-field--outlined fieldset {
    border-color: #E5E7EB !important;
    border-radius: 8px !important;
  }
  .v-input > .v-input__control > .v-input__slot > .v-text-field__slot > input, label {
    font-size: 0.8125rem;
    color: #111827;
  }
  .custom-text-field input[type='number'] {
    -moz-appearance:textfield;
  }
  .custom-text-field input::-webkit-outer-spin-button,
  .custom-text-field input::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
}
</style>
